import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { X } from 'lucide-react';

const SHOTS = [
    {
        src: '/assets/shot2.webp',
        alt: 'Daily nutrition score, AI snapshot and macro grid',
        kicker: 'Dashboard',
        caption: 'Daily nutrition score',
    },
    {
        src: '/assets/shot3.webp',
        alt: 'AI nutrition assistant daily outlook',
        kicker: 'Assistant',
        caption: 'AI nutrition assistant',
    },
];

const LOGS = [
    {
        image: '/assets/food/biryani.webp',
        name: 'Rice & chicken curry',
        kcal: '587 Kcal',
        time: '9:30 pm',
    },
    {
        image: '/assets/food/meal-4.webp',
        name: 'Vegetable pulao',
        kcal: '613 Kcal',
        time: '1:10 pm',
    },
    {
        image: '/assets/food/berry-bowl.webp',
        name: 'Açaí and berry bowl',
        kcal: '464 Kcal',
        time: '8:05 am',
    },
];

const ScreenshotsSection: React.FC = () => {
    const [lightbox, setLightbox] = useState<(typeof SHOTS)[number] | null>(null);

    useEffect(() => {
        if (!lightbox) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setLightbox(null);
        };
        document.addEventListener('keydown', onKey);
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.removeEventListener('keydown', onKey);
            document.body.style.overflow = prev;
        };
    }, [lightbox]);

    return (
        <section
            id="screens"
            className="relative z-[1] scroll-mt-24 border-t border-dark-gunmetal px-5 py-[clamp(64px,9vw,120px)]"
        >
            <div className="mx-auto max-w-[1180px]">
                <div className="flex flex-wrap items-end justify-between gap-5">
                    <div className="max-w-[660px]">
                        <span className="text-[10px] font-medium uppercase tracking-[0.16em] text-success">
                            See Fork in action
                        </span>
                        <h2 className="mt-3.5 text-[clamp(28px,3.8vw,48px)] font-extrabold leading-[1.04] tracking-[-0.035em] text-white [text-wrap:balance]">
                            The interface, exactly as it ships
                        </h2>
                    </div>
                    <p className="max-w-[36ch] text-[15.5px] leading-[1.6] text-dark-textSecondary">
                        Explore the interface and features that make nutrition tracking effortless.
                    </p>
                </div>

                <div className="mt-[clamp(28px,4vw,48px)] grid items-start gap-[clamp(18px,3vw,32px)] md:grid-cols-2 lg:grid-cols-3">
                    {SHOTS.map((shot) => (
                        <figure key={shot.src} className="m-0 grid justify-items-center gap-4">
                            <button
                                onClick={() => setLightbox(shot)}
                                aria-label={`Enlarge: ${shot.caption}`}
                                className="w-full max-w-[280px] cursor-zoom-in rounded-[40px] bg-phone-frame p-2 shadow-[0_34px_76px_rgba(0,0,0,0.5),0_0_0_1px_rgba(255,255,255,0.06)] transition-transform duration-300 hover:-translate-y-2 active:scale-[0.98]"
                            >
                                <span className="relative block overflow-hidden rounded-[34px] bg-black">
                                    <span
                                        aria-hidden="true"
                                        className="absolute left-1/2 top-2.5 z-[3] block h-[23px] w-20 -translate-x-1/2 rounded-full bg-black"
                                    />
                                    <img
                                        src={shot.src}
                                        alt={shot.alt}
                                        loading="lazy"
                                        className="w-full"
                                    />
                                </span>
                            </button>
                            <figcaption className="text-center">
                                <span className="block text-[10px] font-medium uppercase tracking-[0.14em] text-dark-textSubtle">
                                    {shot.kicker}
                                </span>
                                <span className="mt-1.5 block text-base font-semibold text-white">
                                    {shot.caption}
                                </span>
                            </figcaption>
                        </figure>
                    ))}

                    {/* today's logs */}
                    <div className="grid min-w-0 gap-3">
                        <div className="flex items-center">
                            <span className="text-[18px] font-semibold tracking-[0.18px] text-white">
                                Today's logs
                            </span>
                        </div>

                        {LOGS.map((log) => (
                            <div
                                key={log.name}
                                className="flex items-center rounded-3xl bg-[#303539] p-2"
                            >
                                <span
                                    className="h-[72px] w-[72px] shrink-0 rounded-2xl bg-cover bg-center bg-no-repeat"
                                    style={{ backgroundImage: `url(${log.image})` }}
                                />
                                <span className="ml-3 block min-w-0 flex-1">
                                    <span className="block truncate text-base font-medium tracking-[0.16px] text-white">
                                        {log.name}
                                    </span>
                                    <span className="mt-1 inline-flex items-center gap-1.5 rounded-xl bg-[#3F4346] px-2 py-1.5">
                                        <img
                                            src="/assets/app/fire.png"
                                            alt=""
                                            aria-hidden="true"
                                            className="h-[18px] w-[18px]"
                                        />
                                        <span className="text-sm font-semibold tracking-[0.14px] text-white">
                                            {log.kcal}
                                        </span>
                                    </span>
                                </span>
                                <span className="ml-2 self-start whitespace-nowrap rounded-[88px] bg-[#202428] px-1.5 py-1 text-[10px] font-medium tracking-[0.2px] text-success">
                                    {log.time}
                                </span>
                            </div>
                        ))}

                        <div className="rounded-xl border-[0.8px] border-dark-gunmetal bg-[#303539] p-[18px]">
                            <p className="text-[15px] leading-[1.6] text-dark-textSecondary">
                                Every log keeps its photo, so a week later you still know exactly what
                                you ate.
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {createPortal(
                <AnimatePresence>
                    {lightbox && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        onClick={() => setLightbox(null)}
                        role="dialog"
                        aria-modal="true"
                        aria-label={lightbox.caption}
                        className="fixed inset-0 z-[100] flex items-center justify-center bg-black/85 p-6 backdrop-blur-sm"
                    >
                        <button
                            onClick={() => setLightbox(null)}
                            aria-label="Close"
                            className="absolute right-5 top-5 flex h-10 w-10 items-center justify-center rounded-full border border-white/15 bg-black/60 text-white"
                        >
                            <X className="h-5 w-5" />
                        </button>
                        <motion.img
                            initial={{ scale: 0.94 }}
                            animate={{ scale: 1 }}
                            exit={{ scale: 0.94 }}
                            src={lightbox.src}
                            alt={lightbox.alt}
                            onClick={(e) => e.stopPropagation()}
                            className="max-h-[88vh] w-auto rounded-3xl"
                        />
                    </motion.div>
                    )}
                </AnimatePresence>,
                document.body
            )}
        </section>
    );
};

export default ScreenshotsSection;
